import React from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Drawer from "@mui/material/Drawer";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";

const inputSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  height: "45px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

function BooleanValue({ value }) {
  return (
    <span
      className={`inline-flex rounded-md px-2.5 py-1 text-sm font-semibold ${
        value
          ? "bg-emerald-50 text-emerald-700"
          : "bg-rose-50 text-rose-700"
      }`}
    >
      {value ? "True" : "False"}
    </span>
  );
}

function LaborDriverLocationValues({
  type,
  item,
  readOnly = false,
  onSave,
}) {
  const [selectedStoreValue, setSelectedStoreValue] = React.useState(null);
  const [showInherited, setShowInherited] = React.useState(false);
  const [formAttempted, setFormAttempted] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const isFlag = type === "flag";
  const storeValues = isFlag
    ? item?.all_stores_with_labor_program_flags || []
    : item?.labor_driver_attributes_all_stores || [];
  const companyDefault = isFlag ? item?.default : item?.value;
  const sortedStoreValues = [...storeValues].sort((left, right) => {
    if (Boolean(left.is_override) !== Boolean(right.is_override)) {
      return left.is_override ? -1 : 1;
    }
    return String(left.store?.store_name || "").localeCompare(
      String(right.store?.store_name || "")
    );
  });
  const overrideCount = sortedStoreValues.filter(
    (storeValue) => storeValue.is_override
  ).length;
  const inheritedCount = sortedStoreValues.length - overrideCount;
  const canCollapseInherited = overrideCount > 0 && inheritedCount > 0;
  const visibleStoreValues = !canCollapseInherited || showInherited
    ? sortedStoreValues
    : sortedStoreValues.filter((storeValue) => storeValue.is_override);

  React.useEffect(() => {
    setShowInherited(overrideCount === 0);
  }, [item?.uuid, overrideCount]);

  const closeDrawer = () => {
    setSelectedStoreValue(null);
    setFormAttempted(false);
  };

  const openDrawer = (storeValue) => {
    const valueKey = isFlag ? "override" : "value";
    setSelectedStoreValue({
      ...storeValue,
      original_value: storeValue[valueKey],
    });
    setFormAttempted(false);
  };

  const handleValueChange = (event) => {
    const nextValue = event.target.value;
    setSelectedStoreValue((current) => ({
      ...current,
      [isFlag ? "override" : "value"]: nextValue,
    }));
  };

  const selectedValue = selectedStoreValue?.[isFlag ? "override" : "value"];
  const normalizedSelectedValue = isFlag
    ? Boolean(selectedValue)
    : Number(selectedValue);
  const valueIsValid = isFlag
    ? typeof selectedValue === "boolean"
    : selectedValue !== "" &&
      selectedValue !== null &&
      selectedValue !== undefined &&
      Number.isFinite(normalizedSelectedValue);
  const valueHasChanged =
    selectedStoreValue !== null &&
    normalizedSelectedValue !== selectedStoreValue.original_value;
  const matchesCompanyDefault =
    valueIsValid && normalizedSelectedValue === companyDefault;

  const handleSave = async () => {
    setFormAttempted(true);
    if (!selectedStoreValue || !valueIsValid || !valueHasChanged) return;
    setSaving(true);
    try {
      if (matchesCompanyDefault) {
        if (isFlag) {
          await onSave(null, selectedStoreValue.store.id, item.uuid);
        } else {
          await onSave(item.uuid, null, selectedStoreValue.store.id);
        }
      } else if (isFlag) {
        await onSave(
          normalizedSelectedValue,
          selectedStoreValue.store.id,
          item.uuid
        );
      } else {
        await onSave(
          item.uuid,
          normalizedSelectedValue,
          selectedStoreValue.store.id
        );
      }
      closeDrawer();
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedStoreValue?.is_override) return;
    setSaving(true);
    try {
      if (isFlag) {
        await onSave(null, selectedStoreValue.store.id, item.uuid);
      } else {
        await onSave(item.uuid, null, selectedStoreValue.store.id);
      }
      closeDrawer();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="bg-slate-50 px-6 py-3 sm:px-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {storeValues.length} {storeValues.length === 1 ? "location" : "locations"}
            {overrideCount > 0
              ? ` · ${overrideCount} ${overrideCount === 1 ? "override" : "overrides"}`
              : ""}
          </p>
          {canCollapseInherited ? (
            <button
              type="button"
              aria-expanded={showInherited}
              className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
              onClick={() => setShowInherited((current) => !current)}
            >
              {showInherited
                ? "Hide locations using default"
                : `Show ${inheritedCount} using default`}
            </button>
          ) : null}
        </div>
        {storeValues.length ? (
          <ul className="mt-2 divide-y divide-slate-200 border-t border-slate-200">
            {visibleStoreValues.map((storeValue) => (
              <li
                key={storeValue.store.id}
                className="flex min-h-[52px] items-center justify-between gap-4 px-1 py-2 transition-colors hover:bg-white/70 sm:items-start"
              >
                <span className="min-w-0 truncate text-sm font-semibold text-slate-900">
                  {storeValue.store.store_name}
                </span>
                <div className="flex shrink-0 items-center gap-2">
                  {isFlag ? (
                    <BooleanValue value={storeValue.override} />
                  ) : (
                    <span
                      className={`inline-flex h-7 items-center rounded-md px-2.5 text-sm font-semibold ${
                        storeValue.is_override
                          ? "bg-violet-100 text-violet-700"
                          : "bg-white text-slate-700"
                      }`}
                    >
                      {storeValue.value}
                    </span>
                  )}
                  {storeValue.is_override ? (
                    <span className="text-xs font-semibold text-violet-700">
                      Override
                    </span>
                  ) : null}
                  {!readOnly ? (
                    <button
                      type="button"
                      aria-label={`Edit ${item?.name} for ${storeValue.store.store_name}`}
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-semibold text-slate-600 hover:bg-white hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
                      onClick={() => openDrawer(storeValue)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                      Edit
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-6 text-center text-sm text-slate-500">
            No locations are available for this {isFlag ? "flag" : "attribute"}.
          </p>
        )}
      </div>

      <Drawer open={Boolean(selectedStoreValue)} anchor="right" onClose={closeDrawer}>
        <div className="flex h-full w-[450px] max-w-[100vw] flex-col bg-white">
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-slate-900">
                Edit location value
              </h2>
              <p className="mt-1 truncate text-sm text-slate-500">
                {selectedStoreValue?.store?.store_name} · {item?.name}
              </p>
            </div>
            <button
              type="button"
              aria-label="Close drawer"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
              onClick={closeDrawer}
            >
              <CloseIcon />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <dl className="grid grid-cols-2 gap-4 rounded-xl bg-slate-50 p-4">
              <div className="min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Location
                </dt>
                <dd className="mt-1 truncate text-sm font-semibold text-slate-900">
                  {selectedStoreValue?.store?.store_name}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {isFlag ? "Flag" : "Attribute"}
                </dt>
                <dd className="mt-1 truncate text-sm font-semibold text-slate-900">
                  {item?.name}
                </dd>
              </div>
            </dl>

            <div className="mt-6">
              <div className="flex items-center justify-between gap-3">
                <label className="text-sm font-semibold text-slate-900">
                  Location value
                </label>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${selectedStoreValue?.is_override ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-600"}`}>
                  {selectedStoreValue?.is_override ? "Override" : "Uses default"}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Company default: {isFlag ? (companyDefault ? "True" : "False") : companyDefault}
              </p>
              <div className="mt-3">
                {isFlag ? (
                  <Select
                    fullWidth
                    size="small"
                    value={selectedStoreValue?.override ?? false}
                    onChange={handleValueChange}
                    sx={inputSx}
                    inputProps={{ "aria-label": "Location flag value" }}
                  >
                    <MenuItem value={true}>True</MenuItem>
                    <MenuItem value={false}>False</MenuItem>
                  </Select>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={selectedStoreValue?.value ?? ""}
                    onChange={handleValueChange}
                    onBlur={() => setFormAttempted(true)}
                    error={formAttempted && !valueIsValid}
                    helperText={
                      formAttempted && !valueIsValid
                        ? "Enter a valid numeric value."
                        : ""
                    }
                    inputProps={{
                      step: "any",
                      "aria-label": "Location attribute value",
                    }}
                    InputProps={{ sx: inputSx }}
                  />
                )}
              </div>
              {selectedStoreValue?.is_override ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleReset}
                  className="mt-4 text-sm font-semibold text-violet-700 hover:text-violet-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Reset to company default
                </button>
              ) : null}
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <button
              type="button"
              onClick={closeDrawer}
              className="inline-flex h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
            >
              Cancel
            </button>
            <LoadingButton
              variant="contained"
              loading={saving}
              onClick={handleSave}
              disabled={!valueIsValid || !valueHasChanged}
              sx={{
                minWidth: "150px",
                height: "44px",
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "#6d28d9",
                "&:hover": { backgroundColor: "#5b21b6" },
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Save changes
            </LoadingButton>
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default LaborDriverLocationValues;
